/**
 * Счёт цели — «Где копим» (14.10.2026).
 *
 * У новой цели он назван в форме. У старой — берётся из того, куда шли её
 * взносы: конверт на счёте или последний перевод «В цель». Счёт в архиве или
 * кредитка счётом цели не считаются.
 */
import type { Account, Goal } from '@skoka/core';
import type { AppState } from './store.js';

type State = Pick<AppState, 'accounts' | 'reserves' | 'transactions'>;

/** Счета, на которых можно копить: свои деньги, не кредитка и не архив. */
export const savingAccounts = (accounts: readonly Account[]): Account[] =>
  accounts.filter((a) => !a.archived && a.kind !== 'credit_card');

export function goalAccountId(state: State, goal: Goal): string | null {
  // Сняли руками («Копим на этом счёте» без галочки) — счёта нет, по взносам не угадываем.
  if (goal.accountId === '') return null;
  const live = (id: string | undefined): id is string =>
    !!id && state.accounts.some((a) => a.id === id && !a.archived && a.kind !== 'credit_card');
  if (live(goal.accountId)) return goal.accountId;
  const held = state.reserves.find((r) => r.forKind === 'goal' && r.forId === goal.id && r.amount > 0 && live(r.accountId));
  if (held) return held.accountId;
  const last = state.transactions
    .filter((t) => t.type === 'transfer' && t.link?.kind === 'goal' && t.link.goalId === goal.id && live(t.counterAccountId))
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))[0];
  return last?.counterAccountId ?? null;
}

/** Цели, которые копятся на этом счёте: и активные, и на паузе. */
export function goalsOnAccount(state: State & Pick<AppState, 'goals'>, accountId: string): Goal[] {
  return state.goals.filter((g) => (g.status === 'active' || g.status === 'paused') && goalAccountId(state, g) === accountId);
}

/** Куда копить новую цель по умолчанию: накопительный «не для трат», иначе любой «не для трат». */
export function defaultGoalAccount(accounts: readonly Account[]): string {
  const own = savingAccounts(accounts);
  return own.find((a) => a.kind === 'savings' && !a.isLiquid)?.id ?? own.find((a) => !a.isLiquid)?.id ?? '';
}
